import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Zap, Star, Trophy, Target, Flame } from 'lucide-react';

const XP_ICONS = {
  phrase_view: Zap,
  streak_bonus: Flame,
  daily_goal_complete: Target,
  challenge_complete: Trophy,
  default: Star
};

const XPPopup = ({ xpData, isVisible, onClose }) => {
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    if (isVisible && xpData?.earned > 0) {
      setShowDetails(true);
      const timer = setTimeout(() => {
        setShowDetails(false);
        setTimeout(onClose, 300);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isVisible, xpData, onClose]);

  if (!isVisible || !xpData || xpData.earned <= 0) return null;

  return (
    <AnimatePresence>
      {showDetails && (
        <motion.div
          initial={{ opacity: 0, y: 50, scale: 0.8 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -20, scale: 0.9 }}
          className="fixed bottom-24 right-4 z-50"
        >
          <div className="bg-gradient-to-br from-yellow-500/90 to-orange-500/90 backdrop-blur-sm rounded-xl p-4 shadow-2xl border border-yellow-400/50 min-w-[180px]">
            {/* Main XP Display */}
            <motion.div 
              className="flex items-center justify-center gap-2 mb-2"
              initial={{ scale: 0 }}
              animate={{ scale: [0, 1.2, 1] }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              <Zap className="w-6 h-6 text-white" />
              <span className="text-2xl font-bold text-white">+{xpData.earned} XP</span>
            </motion.div>

            {/* Breakdown */}
            {xpData.breakdown && xpData.breakdown.length > 0 && (
              <div className="space-y-1 mt-3 pt-3 border-t border-white/20">
                {xpData.breakdown.map((item, index) => {
                  const Icon = XP_ICONS[item.reason] || XP_ICONS.default;
                  return (
                    <motion.div 
                      key={index}
                      className="flex items-center justify-between text-white/90 text-xs"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.3 + index * 0.1 }}
                    >
                      <span className="flex items-center gap-1">
                        <Icon className="w-3 h-3" />
                        {item.reason === 'phrase_view' && 'Phrase viewed'}
                        {item.reason === 'streak_bonus' && `${item.streak}-day streak`}
                        {item.reason === 'daily_goal_complete' && 'Goal complete!'}
                        {item.reason === 'challenge_complete' && 'Challenge done!'}
                      </span>
                      <span className="font-medium">+{item.amount}</span>
                    </motion.div>
                  );
                })}
              </div>
            )}

            {/* Level Up Alert */}
            {xpData.level_up && (
              <motion.div 
                className="mt-3 pt-3 border-t border-white/20 text-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                <div className="flex items-center justify-center gap-2 text-white">
                  <Star className="w-5 h-5 fill-white" />
                  <span className="font-bold">LEVEL UP!</span>
                  <Star className="w-5 h-5 fill-white" />
                </div>
                <p className="text-white/80 text-sm mt-1">You reached Level {xpData.level}!</p>
              </motion.div>
            )}

            {/* Total XP */}
            <div className="mt-2 text-center text-white/70 text-xs">
              Total: {xpData.total?.toLocaleString()} XP
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default XPPopup;
